/**
 * The hover peek — the 07.09 mock's card beside the library row the pointer is over.
 *
 * A 236px card at `left: calc(100% + 10px)`, so it stands OUTSIDE the 320px drawer, over the day
 * panel beside it. It never widens the drawer and never scrolls it, because it is
 * `position: absolute` in the rail's own box and takes part in no layout.
 *
 * ── It is decorative, and that is a decision rather than an omission ─────────────────────────
 * `aria-hidden`, `pointer-events: none`, hover only, and no keyboard path. Everything it says that
 * a trainer needs is already reachable without it: the row's accessible name reads "Dodaj do dnia:
 * <full name>", and the row draws the primary group as a named chip. What the peek adds is a
 * PICTURE — the still and the lit figure — plus the secondary groups, which is exactly the material
 * a caption cannot carry well and a screen reader would be read a wall of. A hover card that took
 * focus, or announced itself, would interrupt the one act this drawer exists for: pressing a row.
 *
 * It also must not appear mid-drag; the rail clears it on `dragstart`, at the level that owns the
 * state, rather than every row learning about it.
 *
 * ── There is no `<video>`, and the demo data is why ──────────────────────────────────────────
 * The mock loops a clip for two of its eighteen movements. The catalogue answers `videoUrl` for
 * **zero of its 477** — every movement's `videoKey` is null too — so a `<video>` element here would
 * be a tag with nothing to put in it. What the wire does carry is the video's POSTER frame, for 385
 * of them, which is what `stillUrl` reaches for. Nothing here needs `media-src` in the production
 * CSP, and nothing was widened.
 */

import type { ExerciseListItem } from '~/entities/exercise-library';
import { BodySilhouette } from '~/shared/ui/bodyModel/BodySilhouette';
import { BarbellGlyph } from '~/shared/ui/ExerciseGlyphs';
import photoBox from '~/shared/ui/photoBox.module.css';

import { exercisePeek } from '../model/exercisePeek';

import styles from './RailPeek.module.css';

export interface RailPeekProps {
  readonly item: ExerciseListItem;
  /** The movement's type, resolved by the host. `null` when the backend named none. */
  readonly typeLabel: string | null;
  /** One group's word. The host resolves it — `model/` and `ui/` do not reach i18next. */
  readonly muscleLabel: (group: string) => string;
  /** "główna" / "wspomagająca". Two levels, because the wire has two. */
  readonly levelLabel: (isPrimary: boolean) => string;
  /**
   * Whether a still failed to load, and the setter for it — held by the RAIL, not here.
   *
   * The peek is remounted content rather than a remounted element: it re-fills as the pointer moves
   * down the list, so a `useState` in this file would carry one movement's broken image onto the
   * next. The rail keys the flag by the movement's id, which is the only place that knows which
   * movement is being drawn.
   */
  readonly stillFailed: boolean;
  readonly onStillFailed: () => void;
}

export function RailPeek({
  item,
  typeLabel,
  muscleLabel,
  levelLabel,
  stillFailed,
  onStillFailed,
}: RailPeekProps) {
  const peek = exercisePeek(item);
  const hasStill = peek.stillUrl !== null && !stillFailed;

  return (
    <div className={styles.peek} aria-hidden="true">
      {hasStill ? (
        <img
          className={`${String(styles.media)} ${String(photoBox.photo)}`}
          src={peek.stillUrl ?? ''}
          alt=""
          decoding="async"
          onError={onStillFailed}
        />
      ) : (
        // The same answer the row gives a movement with no photograph, at the card's size: the
        // plate filled with the movement's own hue and the barbell drawn in it. Never an empty box.
        <span
          className={`${String(styles.media)} ${String(styles.mediaFallback)}`}
          style={
            peek.tint === null
              ? undefined
              : {
                  background: `color-mix(in srgb, ${peek.tint} 14%, transparent)`,
                  color: peek.tint,
                }
          }
        >
          <BarbellGlyph className={styles.mediaGlyph} strokeWidth={1.6} />
        </span>
      )}

      <div className={styles.body}>
        <span className={styles.head}>
          <b className={styles.name}>{item.name}</b>
          {typeLabel !== null && (
            <span className={styles.chips}>
              <span className={styles.chip}>
                {typeLabel}
              </span>
            </span>
          )}
        </span>

        <div className={styles.engagement}>
          {/* Both views, always. Half the taxonomy is on the back, and a pair that came and went
            * would resize a card that is centred on its own height — so every row change would
            * move it. `male` is stated rather than chosen: a plan template has no athlete and
            * therefore no sex to read, and the figure here is a diagram of a movement rather than
            * a picture of anyone. */}
          <div className={styles.figures}>
            <BodySilhouette
              side="front"
              gender="male"
              fillBySlug={peek.fillBySlug}
              baseFill="var(--bg-chip)"
            />
            <BodySilhouette
              side="back"
              gender="male"
              fillBySlug={peek.fillBySlug}
              baseFill="var(--bg-chip)"
            />
          </div>

          <div className={styles.pills}>
            {peek.muscles.map((muscle) => (
              <span
                key={muscle.group}
                className={`${String(styles.pill)} ${
                  muscle.isPrimary ? String(styles.pillPrimary) : ''
                }`}
              >
                <i className={styles.pillDot} />
                {muscleLabel(muscle.group)}
                <b className={styles.pillLevel}>{levelLabel(muscle.isPrimary)}</b>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
